import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { ListCheckIcon, ListMinusIcon } from 'lucide-react'
import React from 'react'
import { local } from 'service/api'
import { useStore } from 'store'
import { SelectProps } from './types'

interface IProps
    extends Omit<SelectProps, 'form' | 'isSupervisor' | 'defaultValue'> {
    value?: number[]
    onChange?: React.Dispatch<React.SetStateAction<number[]>>
    /** Não filtrar por empresa e sim por cliente */
    listAll?: boolean
}

export const SelectLocais: React.FC<IProps> = ({
    size = 'small',
    variant = 'filled',
    width = 260,
    children,
    hideWhenSingle = false,
    value: controlledValue,
    onChange: controlledOnChange,
    listAll = false,
    ...props
}) => {
    const empresaId = useStore(state => state.companyId)
    const storeValue = useStore(state => state.locaisIds)
    const setStoreValue = useStore(state => state.setLocaisIds)

    const { data: filteredData = [], isFetched: isFilteredFetched } =
        local.useFetch({
            queryParams: {
                empresaId,
            },
            enabled: !listAll,
        })
    const { data: allData = [], isFetched: isAllFetched } = local.all.useFetch({
        enabled: listAll,
    })
    const data = listAll ? allData : filteredData

    if (hideWhenSingle && data.length <= 1) {
        return null
    }

    const value = controlledValue ?? storeValue,
        onChange = controlledOnChange ?? setStoreValue,
        isFetched = listAll ? isAllFetched : isFilteredFetched

    return (
        <FormControl size={size} variant={variant} sx={{ width }}>
            <InputLabel id="locais-label">Locais</InputLabel>
            <Select
                labelId="locais-label"
                name="locais"
                multiple
                value={value}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 'clamp(300px, 50vh, 400px)',
                            maxWidth: '100%',
                        },
                    },
                }}
                renderValue={() =>
                    data
                        .filter(it => value.includes(it.id))
                        .map(it => it.nomeLocal)
                        .join(', ')
                }
                {...props}
            >
                {children}
                {!data.length && isFetched && (
                    <MenuItem value={-1} disabled>
                        <em>Nenhum local cadastrado</em>
                    </MenuItem>
                )}
                {!!data.length && (
                    <>
                        {value.length < data.length ? (
                            <MenuItem
                                value={-1}
                                onClick={() => {
                                    onChange(data.map(it => it.id))
                                }}
                            >
                                <ListCheckIcon className="mr-2" />
                                <em>Selecionar todos</em>
                            </MenuItem>
                        ) : (
                            <MenuItem
                                value={-1}
                                onClick={() => {
                                    onChange([])
                                }}
                            >
                                <ListMinusIcon className="mr-2" />
                                <em>Desmarcar todos</em>
                            </MenuItem>
                        )}
                        {data.map(({ id, nomeLocal }) => (
                            <MenuItem
                                key={id}
                                value={id}
                                onClick={() =>
                                    onChange(prev =>
                                        prev.includes(id)
                                            ? prev.filter(v => v !== id)
                                            : [...prev, id],
                                    )
                                }
                                selected={value.includes(id)}
                                aria-selected={value.includes(id)}
                                className="aria-selected:bg-primary/30 aria-selected:hover:bg-primary/40"
                            >
                                {nomeLocal}
                            </MenuItem>
                        ))}
                    </>
                )}
            </Select>
        </FormControl>
    )
}
